import { constructMSAImageUrl, constructMSASrcset } from 'helpers';
import ProductUtils from 'helpers/ProductUtils';
import { createCurrencyObj } from 'helpers/DataFormatUtils';

const getCardData = props => {
  const {
    alt = '',
    index,
    imageId,
    imageMap = {},
    mainImage = {},
    isHovered,
    msaImageId,
    msaImageParams,
    originalPrice,
    price = null,
    productRating,
    reviewCount,
    reviewRating,
    thumbnailImageId,
    fullSizeImage = false,
    imageNoBackground = false,
    showRatingStars,
    color,
    brandName,
    productName,
    txAttrFacet_Gender: gendersAttribute,
    computeMultiImageAngles = false
  } = props;

  /**
   * Media props
   */

  const media = {
    imageIndex: index,
    isHovered: false,
    fullSizeImage,
    imageNoBackground,
    mainImage
  };

  const msaId = imageId || thumbnailImageId || msaImageId || null;

  if (msaId) {
    const makeProductHoverImage = () => {
      const { PT01, PT03, TOPP, LEFT, RGHT, BACK } = imageMap;
      return RGHT || TOPP || LEFT || BACK || PT03 || PT01 || msaId;
    };

    const mainImage = msaId;
    const hoverImageId = makeProductHoverImage();
    const hasHoverImage = hoverImageId && mainImage !== hoverImageId;

    media.isHovered = isHovered;

    const imageParams = { ...msaImageParams, width: 768, height: 1024 };

    const makeImage = (image, params) => {
      const url = constructMSAImageUrl(image, params);
      const srcset = constructMSASrcset(image, {
        ...params,
        useWebp: true,
        minWidth: 150
      });

      return {
        alt: alt || [color, brandName, productName].filter(Boolean).join(' '),
        src: url,
        // sizes is not exact, but pretty close.
        // it might make sense to move sizes and aspect ratio to the layout (component?) eventually.
        sizes: '(max-width: 1024px) 50vw, max(25vw, 300px)',
        style: {
          aspectRatio: '3 / 4'
        },
        webp: {
          srcSet: srcset
        }
      };
    };

    media.mainImage = makeImage(mainImage, imageParams);

    // Legacy single hover image is skipped for Zappos PLP and Footer Recos Product Cards,
    // where the multi-image preview supersedes it — so building it would only add a wasted
    // image request on initial page load. 6pm keeps the legacy hover image.
    if (hasHoverImage && !computeMultiImageAngles) {
      media.hoverImage = makeImage(hoverImageId, imageParams);
    }

    // Multi-image preview: expose all ordered, deduplicated angles for the product card.
    // Computed only on the preview surface (Zappos PLP) so 6pm cards,
    // which never consume them, skip the makeImage-per-angle work.
    if (computeMultiImageAngles) {
      const orderedAngles = ProductUtils.getOrderedImageAngles(imageMap, mainImage);
      media.allImages = orderedAngles.map(({ imageId }) => makeImage(imageId, imageParams));
      media.imageAngles = orderedAngles;
    }
  }

  /**
   * Price component props
   */

  const priceData = {};

  const priceObj = createCurrencyObj(price);

  if (price) {
    priceData.price = priceObj;
    priceData.label = priceData.price.string;
  }

  if (priceObj.string && originalPrice) {
    priceData.msrp = createCurrencyObj(originalPrice);
    priceData.onSale = ProductUtils.isStyleOnSale({
      price: priceObj.string,
      originalPrice: priceData.msrp.string
    });
    if (priceData.onSale) {
      const percentOff = ProductUtils.getPercentOff({
        originalPrice: priceData.msrp.string,
        price: priceData.price.string
      });
      priceData.label = `On sale for ${priceData.price.string}. MSRP ${priceData.msrp.string}. ${percentOff}% off`;
    }
  }

  /**
   * Review component props
   */

  const reviews = {};

  if ((showRatingStars && productRating) || reviewRating) {
    const rating = typeof reviewRating === 'number' ? reviewRating : parseInt(productRating, 10);
    const floatRating = rating.toFixed(1);
    reviews.roundedRating = productRating;
    reviews.rating = floatRating;
    reviews.label = floatRating > 0 ? `${floatRating} out of 5 stars` : '';
  }

  if (reviewCount) {
    reviews.ratingCount = reviewCount;
  }

  /**
   * txAttrFacet_Gender component prop
   */
  const gender = ProductUtils.getProductGender(gendersAttribute);

  return { media, price: priceData, reviews, gender };
};

export default getCardData;
